import { createBrowserRouter, Navigate } from "react-router-dom";

// Layouts
import PublicLayout from "./layouts/PublicLayout";
import ProtectedLayout from "./layouts/ProtectedLayout";

// Public pages (no auth required)
import Login from "./pages/login";
import Signup from "./pages/signup";
import LandingPage from "./pages/landing-page";
import Error404 from "./pages/error-404";
import Error503 from "./pages/error-503";
import Error403 from "./pages/error-403";
import ErrorPage from "./pages/error-page";
import DownloadCarePlan from "./pages/care-plan/CarePlanDownloadPage";

// Protected pages (auth required)
import { Home } from "./pages/home/Home";
import { Tasks } from "./pages/tasks/Tasks";
import { Patients } from "./pages/patients/Patients";
import { Schedule } from "./pages/schedule/list/Schedule";
import { PatientProfile } from "./pages/PatientProfile/PatientProfile";
import { ScheduleDetails } from "./pages/schedule/ScheduleDetails";
import { Summary } from "./pages/PatientProfile/Summary";
import { Matrix } from "./pages/PatientProfile/Matrix";
import { PGI } from "./pages/PatientProfile/PGI";
import { History } from "./pages/PatientProfile/History";
import { Barriers } from "./pages/PatientProfile/Barriers";
import { Task } from "./pages/PatientProfile/Task";
import { PatientInfo } from "./pages/PatientProfile/PatientInfo/PatientInfo";
import { ClinicalAlertsPatientDetail } from "./pages/PatientProfile/ClinicalAlertsPatientDetail";
import { AppointmentRedirect } from "./pages/schedule/AppointmentRedirect";
import { PatientAppointments } from "./pages/Appointment/PatientAppointments";
import { CareNoteGrid } from "./pages/care-note/CareNoteGrid";
import { CarePlans } from "./pages/care-plan/CarePlans";
import { CarePlan } from "./pages/PatientProfile/CarePlan";
import { Eligibility } from "./pages/Eligibility/Eligibility";
import Reports from "./pages/reports/Reports";
import PGIAssessmentMappings from "./pages/pgi-assessment-mappings/PGIAssessmentMappings";
import { Administration } from "./pages/Administration/Administration";
import { UserAdministration } from "./pages/Administration/UserAdministration";
import { UserRoleAdministration } from "./pages/Administration/UserRoleAdministration";
import { CommandCenter } from "./pages/CommandCenter/CommandCenter";
import { AssessmentGrid as AssessmentGridV2 } from "./pages/Assessment-v2/Grid.assessment";
import AssessmentForm from "./pages/Assessment-v2/Form.assessment";
import PastAssessments from "./pages/Assessment-v2/Completed.assessments";
import { AssessmentGrid } from "./pages/Assessment/AssessmentGrid";
import { Activity } from "./pages/PatientProfile/activity/Activity";


import { USER_ROLES } from "./constants/UserRoles";
import { useRoleAuthorization } from "./customHooks/useRoleAuthorization";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { RootState } from "./store";
import Spinner from "./components/Spinner";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [isLoaded, setIsLoaded] = useState(false);
  const isAuthorized = useRoleAuthorization(allowedRoles);

  useEffect(() => {
    if (currentUser) {
      setIsLoaded(true);
    }
  }, [currentUser]);

  // Wait until user info is loaded before checking for authorization
  if (!isLoaded) {
    return <Spinner />;
  }

  // Replace with design asset when available
  if (!isAuthorized) {
    return (
      <>
        <Error403 />
      </>
    );
  }

  return <>{children}</>;
};

const router = createBrowserRouter([

  // Public Routes
  {
    element: <PublicLayout />,
    children: [
      {
        path: "",
        element: <LandingPage />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "signup",
        element: <Signup />,
      },
      {
        path: "errorPage",
        element: <ErrorPage />,
      },
      {
        path: "error503",
        element: <Error503 />,
      },
      {
        path: "error403",
        element: <Error403 />,
      },
      {
        path: "careplan/:carePlanId",
        element: <DownloadCarePlan />,
      },
    ],
  },

  // Protected Routes
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      {
        path: "administration",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.ADMINISTRATOR]}>
            <Administration />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="users" replace />,
          },
          {
            path: "users",
            element: <UserAdministration />,
          },
          {
            path: "pgi-mappings",
            element: <PGIAssessmentMappings />,
          },
          {
            path: "user-roles",
            element: <UserRoleAdministration />,
          },
        ],
      },
      {
        path: "home",
        element: <Home />,
      },
      {
        path: "tasks",
        element: <Tasks />,
      },
      {
        path: "tasks/:selectedCoach",
        element: <Tasks />,
      },
      {
        path: "patients",
        element: <Patients />,
      },
      {
        path: "eligible-patients",
        element: <Eligibility />,
      },
      {
        path: "patients/:patientId",
        element: <PatientProfile />,
        children: [
          {
            index: true,
            element: <Summary />,
          },
          {
            path: "summary",
            element: <Summary />,
          },
          {
            path: "patientinfo",
            element: <PatientInfo />,
          },
          {
            path: "task",
            element: <Task />,
          },
          {
            path: "task/:selectedCoach",
            element: <Task />,
          },
          {
            path: "clinical-alerts",
            element: <ClinicalAlertsPatientDetail />,
          },
          {
            path: "metrics",
            element: <Matrix />,
          },
          {
            path: "care-note",
            element: <CareNoteGrid />,
          },
          {
            path: "care-note/:memberFormId",
            element: <CareNoteGrid />,
          },
          {
            path: "assessment",
            element: <AssessmentGrid />,
          },
          {
            path: "assessments",
            element: <AssessmentGridV2 />,
          },
          {
            path: "assessments/:assessmentTypeId/start",
            element: <AssessmentForm />,
          },
          {
            path: "assessments/:assessmentTypeId/resume/:memberFormId",
            element: <AssessmentForm />,
          },
          {
            path: "assessments/:assessmentTypeId/history",
            element: <PastAssessments />,
          },
          {
            path: "pgi",
            element: <PGI />,
          },
          {
            path: "history",
            element: <History />,
          },
          {
            path: "appointments",
            element: <PatientAppointments />,
          },
          {
            path: "appointments/:appointmentId",
            element: <ScheduleDetails />,
          },
          {
            path: "care-plan",
            element: <CarePlan />,
          },
          {
            path: "barriers",
            element: <Barriers />,
          },
          {
            path: "activity",
            element: <Activity />,
          },
        ],
      },
      {
        path: "schedule",
        element: <Schedule />,
      },
      {
        path: "care-plans",
        element: <CarePlans />,
      },
      {
        path: "reports",
        element: <Reports />,
      },
      {
        path: "command-center",
        element: (
          <ProtectedRoute allowedRoles={[USER_ROLES.ADMINISTRATOR]}>
            <CommandCenter />
          </ProtectedRoute>
        ),
      },
      {
        // Legacy URL: redirects to the appointment under its patient's profile.
        path: "schedule/charting/:appointmentId",
        element: <AppointmentRedirect />,
      },
      {
        path: "*",
        element: <Error404 statusCode={0} errMessage="" />,
      },
    ],
  },

]);

export default router;
